import { INTEGRATION_PROVIDERS, IntegrationProvidersSchema } from '@weft/types';
import type { IntegrationConfiguration, IntegrationProviders, IntegrationSettings } from '@weft/types';
import { raiseUnreachable } from '@weft/utils';
import { useIntegrationSettings } from 'apps/app/src/hooks/requests/use-integration-settings';
import { useUpdateIntegrationConfiguration } from 'apps/app/src/hooks/requests/use-update-integration-configuration';
import { runMutation } from 'apps/app/src/utils/vy-mutation';
import React from 'react';
import { HubspotConfiguration } from './configuration-tab-content-components/HubspotConfiguration';
import { NetSuiteConfiguration } from './configuration-tab-content-components/NetSuiteConfiguration/NetSuiteConfiguration';
import { StripeConfiguration } from './configuration-tab-content-components/StripeConfiguration/StripeConfiguration';
import type { HandleUpdateConfiguration, HandleUpdateConfigurationSuccess } from './configuration-tab-content-types';

export type ConfigurationTabContentProps = {
  provider: IntegrationProviders;
};

export const ConfigurationTabContent: React.FC<ConfigurationTabContentProps> = ({ provider }) => {
  const integrationProvider = IntegrationProvidersSchema.parse(provider);
  const { data: integrationData } = useIntegrationSettings(integrationProvider);
  
  const { integrationId, integrationSettings } = integrationData;

  const updateConfigurationMutation = useUpdateIntegrationConfiguration(integrationId);

  const handleSubmit = async (integrationConfiguration: IntegrationConfiguration, handleMutationSuccess: HandleUpdateConfigurationSuccess) => {
    await runMutation(
      updateConfigurationMutation,
      integrationConfiguration,
      {
        pending: 'Updating configuration...',
        success: 'Configuration updated',
        error: 'Failed to update configuration',
      },
      {
        onSuccess: handleMutationSuccess,
      });
  };

  return (
    <div className='h-screen min-h-fit bg-[#F6F9FA]'>
      <div className='relative flex h-fit min-h-fit flex-col gap-5'>
        {renderConfigurationComponent(integrationSettings, handleSubmit)}
      </div>
    </div>
  );
};

const renderConfigurationComponent = (settings: IntegrationSettings, handleSubmit: HandleUpdateConfiguration) => {
  const { integrationProvider, integrationConfiguration } = settings;
  
  switch (integrationProvider) {
    case INTEGRATION_PROVIDERS.stripe:
      return (
        <StripeConfiguration apiToken={settings.bearerToken} />
      );
    case INTEGRATION_PROVIDERS.hubspot:
      return <HubspotConfiguration configuration={integrationConfiguration} />;
    case INTEGRATION_PROVIDERS.netSuite:
      return (
        <NetSuiteConfiguration
          configuration={integrationConfiguration}
          handleSubmit={handleSubmit}
        />
      );
    default:
      return raiseUnreachable(integrationProvider);
  }
};